import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import dweLogo from "../../assets/images/webLogin/dweLogo.svg";
import playText from "../../assets/images/webLogin/play.svg";
import background from "../../assets/images/webLogin/background.png";
import eTabWelcome from "../../assets/images/webLogin/eTabWelcome.png";

import CopyrightFooter from "../../components/copyrightFooter/CopyrightFooter";
import Loader from "../../components/ui/Loader";
import Text from "../../components/ui/Text";
import PillButton from "../../components/ui/PillButton";
import useStore from "../../hooks/store/useStore";
import changeLanguageByMarket from "../../locales/utils/changeLanguageByMarket";
import { webLoginStorageKey } from "../../constants/storage";
import useEmailLogin from "../../services/webLogin/useEmailLogin";
import usePhoneNumberLogin from "../../services/webLogin/usePhoneNumberLogin";
import useVerifyDirectLogin from "../../services/webLogin/useVerifyDirectLogin";
import {
  EmailLoginResponse,
  PhoneNumberLoginResponse,
  VerifyDirectLoginResponse,
} from "../../services/webLogin/types";
import setTokenToStorage from "../../services/utils/setTokenToStorage";
import importFont from "../../styles/utils/importFont";
import cn from "../../utils/cn";
import { MarketType } from "../../types";

import AccountInput from "./components/AccountInput";
import ErrorDialogJP, { ErrorDialogType } from "./components/ErrorDialogJP";
import SmsDrawer from "./components/SmsDrawer";
import ToggleAccountTypeJP from "./components/ToggleAccountTypeJP";
import { CountDownTime, CountDownTimeJP } from "./constants";
import useResendCooldownJP from "./hooks/useResendCooldownJP";
import clearDirectLoginParams from "./utils/clearDirectLoginParams";
import getDirectLoginParams from "./utils/getDirectLoginParams";
import isValidPhoneNumber from "./utils/isValidPhoneNumber";
import openApp from "./utils/openApp";
import rewriteLoginPath from "./utils/rewriteLoginPath";
import { AccountType } from "./types";

export default function WebLogin({
  market,
  jpDirectLogin,
}: {
  market: MarketType;
  jpDirectLogin: boolean;
}) {
  const { t } = useTranslation();

  const [fontLoaded, setFontLoaded] = useState(false);
  const [accountType, setAccountType] = useState<AccountType>(
    market === "JP" ? "mail" : "phoneNumber",
  );
  const [account, setAccount] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showSmsDrawer, setShowSmsDrawer] = useState(false);
  const [smsCode, setSmsCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [isFirstFocused, setIsFirstFocused] = useState(false);
  const [isVerifyingDirectLogin, setIsVerifyingDirectLogin] =
    useState(jpDirectLogin);
  const [errorDialogJP, setErrorDialogJP] = useState<ErrorDialogType | null>(
    null,
  );

  const { isResendBlocked, resendBlockedMessageJP, startResendCooldown } =
    useResendCooldownJP(market, account);

  const accountErrorMessage = resendBlockedMessageJP || errorMessage;

  const { language, setLanguage } = useStore((state) => state.global);
  const { setTokens } = useStore((state) => state.tokens);

  const mutationForPhoneLogin = usePhoneNumberLogin();
  const mutationForEmailLogin = useEmailLogin();
  const verifyDirectLoginMutation = useVerifyDirectLogin();

  const showError = (dialog: ErrorDialogType, message: string) => {
    if (market === "JP") return setErrorDialogJP(dialog);

    setErrorMessage(message);
  };

  const handleLoginError = () => {
    showError("systemError", "Login failed");
  };

  const handleLoginSuccess = (
    data: EmailLoginResponse | PhoneNumberLoginResponse,
  ) => {
    const { unLoginType } = data;

    if (errorMessage) setErrorMessage("");

    if (unLoginType === "Member") return openApp(market);

    if (unLoginType === "EmailInvalid")
      return setErrorMessage(t("invalidEmailFormat"));

    if (unLoginType === "PhoneInvalid")
      return setErrorMessage(t("invalidPhoneFormat"));

    if (unLoginType === "InternalError")
      return showError("systemError", unLoginType);

    if (unLoginType === "ServiceUnavailable")
      return showError("maintenance", unLoginType);

    setTimeLeft(market === "JP" ? CountDownTimeJP : CountDownTime);
    startResendCooldown();
    setShowSmsDrawer(true);
  };

  const handleSwitchLoginMethod = () => {
    setAccountType((current) => (current === "mail" ? "phoneNumber" : "mail"));
    setAccount("");
    setErrorMessage("");
  };

  const handleSubmit = () => {
    // Validate phone format on the frontend so numbers from other markets can't slip in; email format is validated by the API.
    if (accountType === "phoneNumber" && !isValidPhoneNumber(account, market))
      return setErrorMessage(t("invalidPhoneFormat"));

    const options = {
      onSuccess: (data: EmailLoginResponse | PhoneNumberLoginResponse) =>
        handleLoginSuccess(data),
      onError: handleLoginError,
    };

    if (accountType === "mail") {
      mutationForEmailLogin.mutate(account, options);
    } else mutationForPhoneLogin.mutate(account, options);
  };

  const handleDirectLoginSuccess = (data: VerifyDirectLoginResponse) => {
    const { success, accessKey, jwtToken, unLoginType } = data;

    clearDirectLoginParams();

    if (success && accessKey && jwtToken) {
      setTokenToStorage(webLoginStorageKey, { accessKey, jwtToken });
      setTokens({ accessKey, jwtToken });

      return;
    }

    setIsVerifyingDirectLogin(false);

    if (unLoginType === "Member") return openApp(market);

    if (unLoginType === "InternalError") return setErrorDialogJP("systemError");

    if (unLoginType === "ServiceUnavailable")
      return setErrorDialogJP("maintenance");

    setErrorDialogJP("loginFailed");
  };

  const handleDirectLoginError = () => {
    clearDirectLoginParams();
    setErrorDialogJP("systemError");
    setIsVerifyingDirectLogin(false);
  };

  useEffect(() => {
    changeLanguageByMarket(market, setLanguage);

    if (!jpDirectLogin) return;

    verifyDirectLoginMutation.mutate(getDirectLoginParams(), {
      onSuccess: handleDirectLoginSuccess,
      onError: handleDirectLoginError,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!language) return;

    importFont(language, setFontLoaded);
  }, [language]);

  // Need to remove this when Japan Team don't use /login path anymore.
  rewriteLoginPath();

  if (isVerifyingDirectLogin || !language || !fontLoaded) return <Loader />;

  return (
    <div className="relative flex min-h-screen w-full justify-center bg-pink1">
      <div className="relative flex min-h-screen w-full min-w-[320px] max-w-[480px] flex-col overflow-x-hidden">
        <img
          src={dweLogo}
          alt="dweLogo"
          className="relative z-[2] mx-4 mt-2 w-32 tablet:mx-8 tablet:mt-4 tablet:w-[200px]"
        />

        <div className="relative z-[2] mx-10 mt-4 tablet:mx-8 tablet:mt-10">
          <img
            src={playText}
            alt="play"
            className="w-[136px] tablet:w-[200px]"
          />
          <Text
            lg
            className="mx-1 mt-3 font-medium leading-[22px] tablet:mx-2 tablet:text-[20px] tablet:font-semibold tablet:leading-[26px]"
          >
            {t("loginSlogan")}
          </Text>
        </div>

        <div className="flex w-full flex-1 flex-col justify-start tablet:justify-center">
          <div className="relative w-full">
            <div className="relative h-[360px] w-full overflow-hidden tablet:h-[450px]">
              <div className="absolute bottom-0 left-1/2 h-[250px] w-[220px] -translate-x-1/2 overflow-hidden tablet:h-[313px] tablet:w-[275px]">
                <img
                  src={eTabWelcome}
                  alt="eTabWelcome"
                  className={cn(
                    "h-full w-auto object-cover transition-all duration-500",
                    isFirstFocused ? "object-left" : "object-right",
                  )}
                />
              </div>

              <img
                src={background}
                alt="background"
                className="absolute left-1/2 z-[1] h-full w-auto -translate-x-1/2 object-cover"
              />
            </div>

            <div className="relative flex w-full flex-col items-center bg-[linear-gradient(9deg,var(--tw-gradient-stops))] from-pink1/30 from-[42%] via-pink2 via-[140%] to-orange to-[246%] px-10 pt-8">
              <AccountInput
                key={accountType}
                accountType={accountType}
                setInputValue={setAccount}
                errorMessage={accountErrorMessage}
                isFirstFocused={isFirstFocused}
                setIsFirstFocused={setIsFirstFocused}
              />

              {accountErrorMessage ? (
                <Text normal className="mt-1 w-full text-red0">
                  {accountErrorMessage}
                </Text>
              ) : null}

              {market === "HK" ? (
                <Text className="mb-8 mt-2 w-full text-[13px] font-medium text-black6">
                  要體驗APP的澳門家長，請手動填上區號853以收取驗證碼。
                </Text>
              ) : (
                <div className="mb-8" />
              )}

              <PillButton
                disabled={account.length === 0 || isResendBlocked}
                text={t("startTrial")}
                className="h-12"
                bgColor="bg-disneyGold"
                onClick={handleSubmit}
                isLoading={
                  mutationForEmailLogin.isLoading ||
                  mutationForPhoneLogin.isLoading
                }
              />

              {market === "JP" ? (
                <ToggleAccountTypeJP
                  accountType={accountType}
                  onClick={handleSwitchLoginMethod}
                />
              ) : null}
            </div>
          </div>
        </div>

        <CopyrightFooter className="mt-auto pb-2" />
      </div>

      <SmsDrawer
        accountType={accountType}
        account={account}
        showSmsDrawer={showSmsDrawer}
        setShowSmsDrawer={setShowSmsDrawer}
        smsCode={smsCode}
        setSmsCode={setSmsCode}
        timeLeft={timeLeft}
        setTimeLeft={setTimeLeft}
        onResendSuccess={startResendCooldown}
        market={market}
        setErrorDialogJP={setErrorDialogJP}
      />

      {errorDialogJP ? (
        <ErrorDialogJP
          type={errorDialogJP}
          onClose={() => setErrorDialogJP(null)}
        />
      ) : null}
    </div>
  );
}
